import type { AiExtraction } from '@/shared/capture/capture-session';
import { AI_EXTRACTION_JOB_TYPES, AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';
import {
  INVENTORIZE_EXTRACTION_BACKEND_STATUSES,
  type InventorizeExtractionBackendStatus,
  type ServerInventorizeExtractionJobOutput,
} from '@/shared/ocr/inventorize-extraction-contract';

export function isServerInventorizeExtractionJobOutput(
  output: unknown,
): output is ServerInventorizeExtractionJobOutput {
  return isRecord(output) && output.mode === 'server_job' && typeof output.jobId === 'string';
}

export function mapBackendStatusToLocalStatus(status: InventorizeExtractionBackendStatus): AiExtraction['status'] {
  switch (status) {
    case INVENTORIZE_EXTRACTION_BACKEND_STATUSES.completed:
      return AI_EXTRACTION_STATUSES.succeeded;
    case INVENTORIZE_EXTRACTION_BACKEND_STATUSES.failed:
      return AI_EXTRACTION_STATUSES.failed;
    case INVENTORIZE_EXTRACTION_BACKEND_STATUSES.running:
      return AI_EXTRACTION_STATUSES.running;
    default:
      return AI_EXTRACTION_STATUSES.queued;
  }
}

export function getLatestInventorizeExtractionJob(extractions: AiExtraction[]) {
  return extractions
    .filter(extraction => extraction.jobType === AI_EXTRACTION_JOB_TYPES.geminiExtraction)
    .filter(extraction => isServerInventorizeExtractionJobOutput(extraction.output))
    .toSorted((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
}

export function getLatestCompletedInventorizeExtraction(extractions: AiExtraction[]) {
  const job = getLatestInventorizeExtractionJob(extractions);

  if (job?.status !== AI_EXTRACTION_STATUSES.succeeded || !isServerInventorizeExtractionJobOutput(job.output)) {
    return undefined;
  }

  return job.output.result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
